<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { centsToWholeUnits, formatPercent, formatWholeNumber } from '@/utils/poolaReportFormat.js';

const props = defineProps({
  // One row from useSupplierDealReport's composePerOffer — id, displayTitle,
  // photoUrl, completed, piecesTotal, piecesClaimed, sellThroughFraction,
  // cashCents, poolshareCents, distinctBuyerCount, poolcreditReturnedCents
  // (the last two are null when unmatched / not-completed → render "—").
  offer: { type: Object, required: true },
});

const { t } = useI18n({ useScope: 'global' });

// Only a completed offer with a matched aggregate row funded poolcredit; a
// nested ternary in the template fights prettier/eslint's Vue formatting
// rules, so it's resolved here instead.
const poolcreditIsZero = computed(() => props.offer.poolcreditReturnedCents == null);
const poolcreditDisplay = computed(() =>
  poolcreditIsZero.value ? '—' : centsToWholeUnits(props.offer.poolcreditReturnedCents),
);
// Precomputed (rather than a multi-attribute class + :class binding in the
// template) so the <span> stays a single line — a multi-line opening tag
// there puts prettier's and eslint-plugin-vue's closing-bracket rules at odds.
const poolcreditValueClass = computed(() => [
  'report-offer-card__stat-value',
  'num',
  { 'report-offer-card__stat-value--zero': poolcreditIsZero.value },
]);
</script>

<template>
  <f7-card class="report-offer-card">
    <f7-card-content>
      <div class="report-offer-card__top">
        <img
          v-if="offer.photoUrl"
          :src="offer.photoUrl"
          :alt="offer.displayTitle"
          class="report-offer-card__thumb"
          loading="lazy"
        />
        <div v-else class="report-offer-card__thumb report-offer-card__thumb--placeholder">
          <f7-icon ios="f7:bag_fill" md="material:shopping_bag" size="18"></f7-icon>
        </div>
        <div class="report-offer-card__title-wrap">
          <p class="report-offer-card__title">{{ offer.displayTitle }}</p>
          <p class="report-offer-card__qty">
            <span class="num">{{ formatWholeNumber(offer.piecesClaimed) }}</span> /
            <span class="num">{{ formatWholeNumber(offer.piecesTotal) }}</span>
          </p>
        </div>
        <span
          class="report-offer-card__chip"
          :class="
            offer.completed ? 'report-offer-card__chip--done' : 'report-offer-card__chip--left'
          "
        >
          {{
            offer.completed
              ? t('poola.report.table.chips.completed')
              : t('poola.report.table.chips.remaining')
          }}
        </span>
      </div>

      <div class="report-offer-card__bar-row">
        <div
          class="report-offer-card__bar"
          :class="
            offer.completed ? 'report-offer-card__bar--teal' : 'report-offer-card__bar--violet'
          "
        >
          <span :style="{ width: formatPercent(offer.sellThroughFraction) }" />
        </div>
        <span class="report-offer-card__pct num">{{
          formatPercent(offer.sellThroughFraction)
        }}</span>
      </div>

      <div class="report-offer-card__stats">
        <div class="report-offer-card__stat">
          <span class="report-offer-card__stat-key">{{
            t('poola.report.table.columns.cash')
          }}</span>
          <span class="report-offer-card__stat-value num">{{
            centsToWholeUnits(offer.cashCents)
          }}</span>
        </div>
        <div class="report-offer-card__stat">
          <span class="report-offer-card__stat-key">{{
            t('poola.report.table.columns.poolshare')
          }}</span>
          <span class="report-offer-card__stat-value num">{{
            centsToWholeUnits(offer.poolshareCents)
          }}</span>
        </div>
        <div class="report-offer-card__stat">
          <span class="report-offer-card__stat-key">{{
            t('poola.report.table.columns.poolcredit')
          }}</span>
          <span :class="poolcreditValueClass">{{ poolcreditDisplay }}</span>
        </div>
        <div class="report-offer-card__stat">
          <span class="report-offer-card__stat-key">{{
            t('poola.report.table.columns.buyers')
          }}</span>
          <span class="report-offer-card__stat-value num">{{
            offer.distinctBuyerCount == null ? '—' : formatWholeNumber(offer.distinctBuyerCount)
          }}</span>
        </div>
      </div>
    </f7-card-content>
  </f7-card>
</template>

<style scoped>
.num {
  font-variant-numeric: tabular-nums;
}

.report-offer-card {
  margin: 0 var(--poolit-spacing-md, 16px) var(--poolit-spacing-md, 16px);
  border-radius: var(--poolit-radius-lg, 16px);
}

.report-offer-card__top {
  display: flex;
  align-items: center;
  gap: 11px;
}

.report-offer-card__thumb {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  object-fit: cover;
  flex: none;
}

.report-offer-card__thumb--placeholder {
  background: var(--report-surface-2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--report-text-mute);
}

.report-offer-card__title-wrap {
  min-width: 0;
  flex: 1;
}

.report-offer-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.3;
  color: var(--report-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-offer-card__qty {
  margin: 1px 0 0;
  font-size: 11.5px;
  color: var(--report-text-soft);
}

.report-offer-card__chip {
  margin-inline-start: auto;
  flex: none;
  font-size: 11.5px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  padding: 5px 10px;
  border-radius: var(--poolit-radius-full, 9999px);
}

.report-offer-card__chip--done {
  background: var(--report-teal-bg);
  color: var(--report-teal-deep);
}

.report-offer-card__chip--left {
  background: var(--report-surface-2);
  color: var(--report-text-soft);
}

.report-offer-card__bar-row {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 10px;
}

.report-offer-card__bar {
  flex: 1;
  height: 9px;
  border-radius: var(--poolit-radius-full, 9999px);
  background: var(--report-surface-2);
  overflow: hidden;
}

.report-offer-card__bar > span {
  display: block;
  height: 100%;
  border-radius: var(--poolit-radius-full, 9999px);
}

.report-offer-card__bar--teal > span {
  background: var(--report-teal);
}

.report-offer-card__bar--violet > span {
  background: var(--report-violet);
}

.report-offer-card__pct {
  font-size: 11.5px;
  color: var(--report-text-soft);
  font-weight: var(--poolit-font-weight-semibold, 600);
  min-width: 34px;
  text-align: start;
}

.report-offer-card__stats {
  display: flex;
  gap: 6px;
  border-block-start: 1px solid var(--report-hairline);
  padding-top: 10px;
  margin-top: 10px;
}

.report-offer-card__stat {
  flex: 1;
  text-align: center;
}

.report-offer-card__stat-key {
  display: block;
  font-size: 10px;
  color: var(--report-text-mute);
}

.report-offer-card__stat-value {
  display: block;
  margin-top: 2px;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
}

.report-offer-card__stat-value--zero {
  color: var(--report-text-mute);
  font-weight: var(--poolit-font-weight-regular, 400);
}
</style>
